// src/pages/ResetPasswordPage.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../services/api";
import { Mail, Lock, BookOpen } from "lucide-react";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const hash = typeof window !== "undefined" ? window.location.hash || "" : "";
  const paramsFromHash = new URLSearchParams(hash.startsWith("#") ? hash.substring(1) : hash);
  const accessToken = paramsFromHash.get("access_token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const header = document.querySelector("header");
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => {
      if (!header) return;
      if (mq.matches) header.classList.add("hidden");
      else header.classList.remove("hidden");
    };
    apply();
    mq.addEventListener("change", apply);
    return () => {
      mq.removeEventListener("change", apply);
      if (header) header.classList.remove("hidden");
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    try {
      setLoading(true);
      const payload = accessToken
        ? { access_token: accessToken, new_password: newPassword }
        : { token, new_password: newPassword };
      const res = await api.post("/api/auth/reset-password", payload);
      setMessage(res.data.message || "Password reset successfully.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Error resetting password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="lg:hidden grid min-h-svh">
        <div className="flex flex-col gap-4 p-6 md:p-10">
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-6">
              <img src="/LogoBeatPost.png" alt="Beatpost" style={{ width: "93px", height: "85px" }} />
              <div className="w-full max-w-xs">
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                  <div className="flex flex-col items-center gap-1 text-center">
                    <h1 className="text-2xl font-bold text-newspaper-900">Reset password</h1>
                    <p className="text-sm text-newspaper-600">Create a new password to continue</p>
                  </div>
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-newspaper-700 mb-2">
                      New password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="beat-input rounded-lg"
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-newspaper-700 mb-2">
                      Confirm password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="beat-input rounded-lg"
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full beat-button flex items-center justify-center space-x-2 py-3 rounded-lg"
                    >
                      {loading ? (
                        <div className="beat-spinner"></div>
                      ) : (
                        <>
                          <Lock className="w-4 h-4" />
                          <span>Reset</span>
                        </>
                      )}
                    </button>
                    {(message || error) && (
                      <div className="text-center">
                        {message && <p className="text-green-600 text-sm">{message}</p>}
                        {error && <p className="text-red-600 text-sm">{error}</p>}
                      </div>
                    )}
                    <p className="text-center text-newspaper-600 mt-3">
                      Remembered your password?{" "}
                      <Link to="/login" className="underline underline-offset-4 text-newspaper-900">
                        Sign in
                      </Link>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden lg:block">
        <div className="beat-container max-w-md mx-auto">
          <div className="beat-card p-8 rounded-lg">
            <div className="text-center mb-8">
              <div className="flex justify-center items-center mb-4">
                <BookOpen className="w-12 h-12 text-newspaper-900" />
              </div>
              <h1 className="text-2xl font-bold text-newspaper-900 mb-2">Reset Password</h1>
              <p className="text-newspaper-600">Enter a new password to access your account</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-newspaper-700 mb-2">
                  New password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="beat-input pl-10 rounded-lg"
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-newspaper-400" />
                </div>
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-newspaper-700 mb-2">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="beat-input pl-10 rounded-lg"
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-newspaper-400" />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full beat-button flex items-center justify-center space-x-2 py-3 rounded-lg"
              >
                {loading ? (
                  <div className="beat-spinner"></div>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    <span>Reset</span>
                  </>
                )}
              </button>
            </form>
            {(message || error) && (
              <div className="mt-6 text-center">
                {message && <p className="text-green-600 text-sm">{message}</p>}
                {error && <p className="text-red-600 text-sm">{error}</p>}
              </div>
            )}
            <div className="mt-6 text-center">
              <p className="text-newspaper-600">
                Back to login?{" "}
                <Link to="/login" className="text-newspaper-900 font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordPage;
